import { logger } from "@/lib/logger";
/**
 * Offline Queue Status Indicator
 * Shows green/orange/red sync status
 * Auto-hides after 30s when synced, reappears on page change or connectivity change
 * Always visible when pending or offline
 * 
 * CRASH-SAFE: All operations wrapped in try-catch, fails silently
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, RefreshCw, WifiOff } from 'lucide-react';
import { getOfflineQueueCount, processOfflineQueue } from '@/utils/offlineEdgeFunctions';
import { offlineFileQueue } from '@/utils/offlineFileQueue';
import { toast } from 'sonner';
import { TooltipWrapper } from '@/components/ui/tooltip-wrapper';
import { OfflineHelpDialog } from './OfflineHelpDialog';
import { cn } from '@/lib/utils';
import { safeIsOnline, hasWindow } from '@/lib/browserSafeUtils';

const AUTO_HIDE_DELAY = 30000; // 30 seconds

type SyncStatus = 'synced' | 'pending' | 'offline';

export function OfflineQueueStatus() {
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(() => safeIsOnline());
  const [edgeFunctionCount, setEdgeFunctionCount] = useState(0);
  const [fileCount, setFileCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const totalCount = edgeFunctionCount + fileCount;

  // Determine sync status
  const getStatus = (): SyncStatus => {
    if (!isOnline) return 'offline';
    if (totalCount > 0) return 'pending';
    return 'synced';
  };

  const status = getStatus();

  // Clear the hide timeout
  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Start the auto-hide timer (only when synced)
  const startHideTimer = useCallback(() => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, AUTO_HIDE_DELAY);
  }, [clearHideTimeout]);

  // Show the indicator and manage auto-hide based on status
  const showIndicator = useCallback(() => {
    setIsVisible(true);
    // If synced, start auto-hide timer; otherwise keep visible
    if (status === 'synced') {
      startHideTimer();
    } else {
      clearHideTimeout();
    }
  }, [status, startHideTimer, clearHideTimeout]);

  const updateCounts = useCallback(async () => {
    try {
      // Get edge function count - already safe
      const efCount = getOfflineQueueCount();
      setEdgeFunctionCount(efCount);
      
      // Get file count - already safe
      const fCount = await offlineFileQueue.getQueueCount();
      setFileCount(fCount);
      
      setHasError(false);
    } catch (error) {
      logger.warn('[OfflineQueueStatus] updateCounts error:', error);
      // Don't crash, just use 0
      setEdgeFunctionCount(0);
      setFileCount(0);
    }
  }, []);

  const handleRetry = useCallback(async () => {
    try {
      if (!safeIsOnline()) {
        toast.error('Still offline', {
          description: 'Cannot retry while offline. Your data is safe locally.',
        });
        return;
      }

      setIsRetrying(true);
      await processOfflineQueue();
      await updateCounts();
      
      if (totalCount === 0) {
        toast.success('All synced!', {
          description: 'Your data is safely stored in the cloud.',
        });
      }
    } catch (error) {
      logger.error('[OfflineQueueStatus] Retry failed:', error);
      toast.error('Sync failed', {
        description: 'Some items could not be synced. They will retry automatically.',
      });
    } finally {
      setIsRetrying(false);
    }
  }, [totalCount, updateCounts]);

  // Show indicator on route change
  useEffect(() => {
    showIndicator();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle status changes - keep visible when not synced
  useEffect(() => {
    if (status !== 'synced') {
      // Not synced - always visible, clear any hide timer
      clearHideTimeout();
      setIsVisible(true);
    } else if (isVisible) {
      // Synced and visible - start auto-hide timer
      startHideTimer();
    }
  }, [status, isVisible, clearHideTimeout, startHideTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearHideTimeout();
    };
  }, [clearHideTimeout]);

  useEffect(() => {
    // Initial count update
    updateCounts();

    const handleOnline = () => {
      try {
        setIsOnline(true);
        updateCounts();
        // Show indicator on connectivity change
        setIsVisible(true);
      } catch (error) {
        logger.warn('[OfflineQueueStatus] handleOnline error:', error);
      }
    };

    const handleOffline = () => {
      try {
        setIsOnline(false);
        updateCounts();
        // Show indicator on connectivity change (will stay visible due to offline status)
        setIsVisible(true);
      } catch (error) {
        logger.warn('[OfflineQueueStatus] handleOffline error:', error);
      }
    };

    const handleQueueUpdate = () => {
      try {
        updateCounts();
      } catch (error) {
        logger.warn('[OfflineQueueStatus] handleQueueUpdate error:', error);
      }
    };

    // Setup listeners safely
    try {
      if (hasWindow()) {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('offline-queue-updated', handleQueueUpdate);
        window.addEventListener('offline-file-queue-updated', handleQueueUpdate);
      }
    } catch (error) {
      logger.warn('[OfflineQueueStatus] Failed to setup listeners:', error);
    }

    // Poll for updates every 10 seconds (but don't force visibility)
    const interval = setInterval(() => {
      try {
        updateCounts();
      } catch (error) {
        logger.warn('[OfflineQueueStatus] Poll error:', error);
      }
    }, 10000);

    return () => {
      try {
        if (hasWindow()) {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
          window.removeEventListener('offline-queue-updated', handleQueueUpdate);
          window.removeEventListener('offline-file-queue-updated', handleQueueUpdate);
        }
        clearInterval(interval);
      } catch (error) {
        logger.warn('[OfflineQueueStatus] Cleanup error:', error);
      }
    };
  }, [updateCounts]);

  // If there was an error, still show basic status without crashing
  if (hasError) {
    return null;
  }

  // Hide when not visible (auto-hidden after 30s when synced)
  if (!isVisible) {
    return null;
  }

  // Status-based configuration
  const statusConfig = {
    synced: {
      bg: 'bg-emerald-500',
      bgLight: 'bg-emerald-50 dark:bg-emerald-950/30',
      border: 'border-emerald-200 dark:border-emerald-800',
      text: 'text-emerald-700 dark:text-emerald-400',
      icon: Check,
      label: 'Synced',
      tooltip: 'All data safely stored in the cloud',
    },
    pending: {
      bg: 'bg-amber-500',
      bgLight: 'bg-amber-50 dark:bg-amber-950/30',
      border: 'border-amber-200 dark:border-amber-800',
      text: 'text-amber-700 dark:text-amber-400',
      icon: RefreshCw,
      label: `${totalCount} pending`,
      tooltip: `${totalCount} item${totalCount > 1 ? 's' : ''} waiting to sync. They'll upload automatically, or click retry.`,
    },
    offline: {
      bg: 'bg-red-500',
      bgLight: 'bg-red-50 dark:bg-red-950/30',
      border: 'border-red-200 dark:border-red-800',
      text: 'text-red-700 dark:text-red-400',
      icon: WifiOff,
      label: 'Offline',
      tooltip: "You're offline. Don't worry - your data is saved locally and will sync when you're back online.",
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  // Comprehensive color explanation tooltip
  const colorExplanationTooltip = `Status Colors:
• Green = All synced to cloud
• Orange = Items waiting to sync
• Red = You're offline (data saved locally)

Current: ${config.tooltip}`;

  return (
    <div className="fixed top-1/2 -translate-y-1/2 right-4 z-50">
      <div className={cn(
        "flex items-center gap-2 rounded-lg px-4 py-2 shadow-lg border",
        config.bgLight,
        config.border
      )}>
        <TooltipWrapper title={colorExplanationTooltip}>
          <div className="flex items-center gap-2 cursor-help">
            {/* Status Icon with colored background */}
            <div className={cn(
              "flex items-center justify-center h-7 w-7 rounded-full",
              config.bg
            )}>
              <Icon className={cn(
                "h-4 w-4 text-white",
                status === 'pending' && isRetrying && "animate-spin"
              )} />
            </div>
            
            <div className="flex flex-col">
              <span className={cn("text-sm font-semibold", config.text)}>
                {status === 'synced' ? 'Synced' : status === 'offline' ? 'Offline' : 'Online'}
              </span>
              <span className="text-xs text-muted-foreground">
                {config.label}
              </span>
            </div>
          </div>
        </TooltipWrapper>

        {totalCount > 0 && isOnline && (
          <TooltipWrapper title="Sync pending items now">
            <Button
              size="sm"
              variant="outline"
              onClick={handleRetry}
              disabled={isRetrying}
              className="h-7 px-2 text-xs"
            >
              <RefreshCw className={cn("h-3 w-3 mr-1", isRetrying && 'animate-spin')} />
              Sync
            </Button>
          </TooltipWrapper>
        )}

        {/* Help Button - wrapped in error boundary */}
        <SafeOfflineHelpDialog isOnline={isOnline} />
      </div>
    </div>
  );
}

/**
 * Safe wrapper for OfflineHelpDialog that won't crash if there's an error
 */
function SafeOfflineHelpDialog({ isOnline }: { isOnline: boolean }) {
  try {
    return (
      <OfflineHelpDialog 
        isOnline={isOnline} 
        triggerClassName="h-7 w-7 p-0"
      />
    );
  } catch (error) {
    logger.warn('[SafeOfflineHelpDialog] Render error:', error);
    return null;
  }
}